import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, IconButton, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Paper, List, ListItem, ListItemText,
  Dialog, DialogTitle, DialogContent, Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StoreIcon from '@mui/icons-material/Store';
import CloseIcon from '@mui/icons-material/Close';
import { apiFetch } from '../apiFetch';

interface StoreStats {
  id: number;
  name: string;
  spending: string;
  trips: number;
}

interface ActivityData {
  period: string;
  total_spending: string;
  total_trips: number;
  stores_count: number;
  stores: StoreStats[];
}

type Period = 'week' | 'month' | '3months' | '6months' | 'ytd' | 'year' | 'all';

const periodLabels: Record<Period, string> = {
  week: 'Week',
  month: 'Month',
  '3months': '3 Months',
  '6months': '6 Months',
  ytd: 'YTD',
  year: '1 Year',
  all: 'All Time',
};

const ShoppingActivity: React.FC = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [period, setPeriod] = useState<Period>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActivityData | null>(null);
  const [storesDialogOpen, setStoresDialogOpen] = useState(false);

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiFetch(`${apiUrl}/api/shopping-activity/?period=${period}`);
        setData(result);
      } catch (err: any) {
        setError(err.message || 'Failed to load shopping activity.');
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [apiUrl, period]);

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: Period | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return `$${num.toFixed(2)}`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      {/* Title row */}
      <Box className="title-row">
        <IconButton onClick={() => navigate('/')} aria-label="back to home">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" className="page-title">
          Shopping Activity
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review your shopping activity and see statistics about your spending across different stores.
      </Typography>

      {/* Period selector */}
      <Box sx={{ mb: 3, overflowX: 'auto' }}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          size="small"
          sx={{ flexWrap: 'nowrap' }}
        >
          {(Object.keys(periodLabels) as Period[]).map((p) => (
            <ToggleButton key={p} value={p} sx={{ whiteSpace: 'nowrap', px: 2 }}>
              {periodLabels[p]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {/* Total Spending */}
            <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: 150 }}>
              <Typography variant="overline" color="text.secondary">
                Total Spending
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(data.total_spending)}
              </Typography>
            </Paper>

            {/* Total Trips */}
            <Paper sx={{ p: 2, flex: '1 1 150px', minWidth: 120 }}>
              <Typography variant="overline" color="text.secondary">
                Shopping Trips
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {data.total_trips}
              </Typography>
            </Paper>

            {/* Stores */}
            <Paper
              sx={{
                p: 2,
                flex: '1 1 150px',
                minWidth: 120,
                cursor: data.stores_count > 0 ? 'pointer' : 'default',
                '&:hover': data.stores_count > 0 ? { bgcolor: 'action.hover' } : {},
              }}
              onClick={() => data.stores_count > 0 && setStoresDialogOpen(true)}
            >
              <Typography variant="overline" color="text.secondary">
                Stores Visited
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {data.stores_count}
                </Typography>
                {data.stores_count > 0 && (
                  <StoreIcon color="action" fontSize="small" />
                )}
              </Box>
            </Paper>
          </Box>

          {/* Store breakdown */}
          {data.stores.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Spending by Store
              </Typography>
              <List disablePadding>
                {data.stores.map((store, index) => (
                  <React.Fragment key={store.id}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={store.name}
                        secondary={`${store.trips} trip${store.trips !== 1 ? 's' : ''}`}
                      />
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(store.spending)}
                      </Typography>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}

          {data.total_trips === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No shopping activity found for this period.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Complete a shopping trip using "Going Shopping" to see your activity here.
              </Typography>
            </Paper>
          )}
        </>
      ) : null}

      {/* Stores Dialog */}
      <Dialog
        open={storesDialogOpen}
        onClose={() => setStoresDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Stores Visited
          <IconButton onClick={() => setStoresDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List disablePadding>
            {data?.stores.map((store, index) => (
              <React.Fragment key={store.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary={store.name} />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ShoppingActivity;
